import React from 'react';
import { createPortal } from 'react-dom';
import { Role, PanelSection, RoleDefaultAccess } from '../../../../../shared/services/userService';

interface RoleDefaultAccessModalProps {
  role: Role | null;
  roleDefaultAccess: RoleDefaultAccess[];
  panelSections: PanelSection[];
  updating: boolean;
  getSectionAccess: (sectionId: string) => boolean;
  onToggleAccess: (section: string, hasAccess: boolean) => void;
  onClose: () => void;
}

const RoleDefaultAccessModal: React.FC<RoleDefaultAccessModalProps> = ({
  role,
  roleDefaultAccess,
  panelSections,
  updating,
  getSectionAccess,
  onToggleAccess,
  onClose
}) => {
  if (!role) return null;

  return createPortal(
    <div className="fixed inset-0 bg-cockpit-bg-2/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="rounded-xl border border-[#223046]/90 bg-[#060a11] p-6 shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-cockpit-text mb-2">
          Accès par défaut — {role.role_name}
        </h3>
        <p className="text-xs text-cockpit-text-2 mb-5">
          Définissez les accès par défaut pour ce rôle. Les surcharges individuelles priment toujours sur ces valeurs.
        </p>

        {panelSections.length === 0 ? (
          <p className="text-cockpit-text-2 text-sm">Chargement des sections...</p>
        ) : (
          <div className="space-y-3">
            {panelSections.map((section) => {
              const hasAccess = getSectionAccess(section.id);
              return (
                <div
                  key={section.id}
                  className="rounded-lg border border-[#223046] bg-[#0b1220]/80 p-4 flex items-center justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="text-cockpit-text font-semibold text-sm">{section.name}</h4>
                    <p className="text-cockpit-text-2 text-xs mt-0.5">{section.description}</p>
                    {roleDefaultAccess.find(a => a.section === section.id) && (
                      <p className="text-cockpit-muted text-xs mt-1">
                        Modifié le : {new Date(roleDefaultAccess.find(a => a.section === section.id)!.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${
                      hasAccess
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                        : 'bg-red-500/20 text-red-300 border-red-500/40'
                    }`}>
                      {hasAccess ? 'Accordé' : 'Refusé'}
                    </span>
                    <button
                      onClick={() => onToggleAccess(section.id, !hasAccess)}
                      disabled={updating}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                        hasAccess
                          ? 'border-red-500/35 bg-red-500/10 text-red-300 hover:bg-red-500/20'
                          : 'border-emerald-500/35 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                      }`}
                    >
                      {updating ? '...' : hasAccess ? 'Retirer' : 'Accorder'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-5 w-full rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 py-2.5 text-sm font-semibold text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>,
    document.body
  );
};

export default RoleDefaultAccessModal;
